import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileText, Shield, Users, Briefcase } from "lucide-react";
import { DashboardHeader } from "@/components/dashboard/dashboard-header";
import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { LegalDocumentTemplate, GeneratedLegalDocument } from "@shared/operations-schema";
import { useSelectedBusiness } from "@/hooks/use-selected-business";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";

export default function LegalDocumentsPage() {
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const { activeBusiness } = useSelectedBusiness();
  const [selectedTab, setSelectedTab] = useState("templates");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);

  const businessId = activeBusiness?.id;

  const {
    data: subscription,
    isLoading: isSubscriptionLoading
  } = useQuery({
    queryKey: ["/api/businesses", businessId, "subscription"],
    queryFn: async () => {
      if (!businessId) return null;
      const res = await apiRequest("GET", `/api/businesses/${businessId}/subscription`);
      return await res.json();
    },
    enabled: !!businessId
  });

  const {
    data: templates,
    isLoading: isTemplatesLoading 
  } = useQuery<LegalDocumentTemplate[]>({
    queryKey: ["/api/legal-document-templates"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/legal-document-templates");
      return await res.json();
    }
  });

  const {
    data: generatedDocuments,
    isLoading: isDocumentsLoading,
    refetch: refetchDocuments
  } = useQuery<GeneratedLegalDocument[]>({
    queryKey: ["/api/businesses", businessId, "legal-documents"],
    queryFn: async () => {
      if (!businessId) return [];
      const res = await apiRequest("GET", `/api/businesses/${businessId}/legal-documents`);
      return await res.json();
    },
    enabled: !!businessId
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: number) => {
      await apiRequest("DELETE", `/api/legal-documents/${documentId}`);
      return documentId;
    },
    onSuccess: () => {
      toast({
        title: "Document deleted",
        description: "The document has been deleted successfully.",
      });
      refetchDocuments();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete document",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const openDeleteDialog = (documentId: number) => {
    setDocumentToDelete(documentId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate(documentToDelete);
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleCreateDocument = (templateType: string) => {
    navigate(`/legal/create/${templateType}`);
  };

  const handleViewDocument = (documentId: number) => {
    navigate(`/legal/view/${documentId}`);
  };

  const handleDownloadDocument = (documentId: number) => {
    window.open(`/api/legal-documents/${documentId}/download`, '_blank');
  };

  // Check if user has ZentroScale subscription (bypassed in development)
  const isPremiumFeature = false; // Development mode: always allow access

  const getTemplatesByType = (type: string) => {
    return templates?.filter(template => template.type === type) || [];
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "Business Contracts":
        return <Briefcase className="h-12 w-12 text-primary" />;
      case "Independent Contractor Agreement":
        return <FileText className="h-12 w-12 text-primary" />;
      case "LLC Agreements":
        return <Shield className="h-12 w-12 text-primary" />;
      case "Partnership Agreements":
        return <Users className="h-12 w-12 text-primary" />;
      default:
        return <FileText className="h-12 w-12 text-primary" />;
    }
  };

  const templateTypes = [
    {
      id: "business-contracts",
      name: "Business Contracts",
      type: "Business Contracts",
      description: "Create standardized business agreements for clients, vendors, or partners"
    },
    {
      id: "contractor-agreements",
      name: "Contractor Agreements",
      type: "Independent Contractor Agreement",
      description: "Generate legally compliant contractor agreements for your freelancers"
    },
    {
      id: "llc-agreements",
      name: "LLC Agreements",
      type: "LLC Agreements",
      description: "Create comprehensive LLC operating agreements for your business"
    },
    {
      id: "partnership-agreements",
      name: "Partnership Agreements",
      type: "Partnership Agreements",
      description: "Establish formal partnership agreements with your business partners"
    }
  ];

  if (isSubscriptionLoading) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Legal Documents" text="Generate and manage legal documents for your business" />
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <Helmet>
        <title>Legal Documents | ZentroBooks</title>
        <meta name="description" content="Generate and manage legal documents for your business with ZentroBooks" />
      </Helmet>
      
      <DashboardHeader 
        heading="Legal Documents" 
        text="Generate and manage legal documents for your business" 
      />

      {isPremiumFeature ? (
        <div className="rounded-lg border bg-amber-50 p-6 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-amber-800 mb-2">Premium Feature</h2>
          <p className="text-amber-700 mb-4">
            The Legal Document Generator is available exclusively with the ZentroScale plan.
          </p>
          <Button onClick={() => navigate("/subscription/plans")}>
            Upgrade to ZentroScale
          </Button>
        </div>
      ) : (
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="templates">Document Templates</TabsTrigger>
            <TabsTrigger value="documents">My Documents</TabsTrigger>
          </TabsList>
          
          <TabsContent value="templates" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {templateTypes.map((templateType) => (
                <Card key={templateType.id} className="flex flex-col h-full">
                  <CardHeader className="flex flex-row items-start gap-4 space-y-0">
                    <div className="flex flex-col gap-1 pt-2">
                      {getIconForType(templateType.type)}
                    </div>
                    <div className="space-y-1">
                      <CardTitle>{templateType.name}</CardTitle>
                      <CardDescription>{templateType.description}</CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <div className="text-sm text-muted-foreground">
                      {isTemplatesLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <p>{getTemplatesByType(templateType.type).length} templates available</p>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="w-full" 
                      onClick={() => handleCreateDocument(templateType.type)}
                      disabled={isTemplatesLoading}
                    >
                      Create Document
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="documents" className="space-y-4">
            {isDocumentsLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : generatedDocuments && generatedDocuments.length > 0 ? (
              <div className="rounded-md border">
                <div className="grid grid-cols-1 divide-y">
                  {generatedDocuments.map((document) => (
                    <div
                      key={document.id}
                      className="flex items-center justify-between p-4"
                    >
                      <div className="flex items-center space-x-4">
                        <FileText className="h-8 w-8 text-blue-500" />
                        <div>
                          <h3 className="font-medium">{document.documentName}</h3>
                          <p className="text-sm text-gray-500">
                            Created on {format(new Date(document.createdAt), "MMM d, yyyy")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDocument(document.id)}
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDocument(document.id)}
                          disabled={!document.documentFileId}
                        >
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => openDeleteDialog(document.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border p-8 text-center">
                <h2 className="text-xl font-semibold mb-2">No Documents Yet</h2>
                <p className="text-muted-foreground mb-4">
                  You haven't created any legal documents yet. Go to the Document Templates tab to create your first document.
                </p>
                <Button onClick={() => setSelectedTab("templates")}>
                  Create Document
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this document. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
